import { ArrowLeft, Copy, Download, Edit, FileText, GitFork, Import, Share } from 'lucide-react'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { PanelBreadcrumbs, type BreadcrumbItem } from './PanelBreadcrumbs'
import { getPanelContentActions, PanelContentRenderer, type PanelContent } from './PanelContent'
import { TopBar, TopBarButton, TopBarToggleSwitch, type TopBarAction } from './TopBar'
import { TopBarActionId } from './TopBarActionIds'

// Context for panel mode (used by workflow execution results)
interface PanelModeContextType {
  mode: string
  setMode: (mode: string) => void
}

const PanelModeContext = createContext<PanelModeContextType | null>(null)

export const usePanelMode = () => {
  const context = useContext(PanelModeContext)
  if (!context) {
    throw new Error('usePanelMode must be used within a Panel with mode support')
  }
  return context
}

interface PanelProps {
  content: PanelContent
  breadcrumbs: BreadcrumbItem[]
  onBack?: () => void
  onNavigateTo?: (index: number) => void
  onAction?: (actionId: string) => void
  onOpenPanel?: (content: PanelContent, breadcrumbs: any[]) => void
  className?: string
  centerContent?: React.ReactNode
}

export function Panel({
  content,
  breadcrumbs,
  onBack,
  onNavigateTo,
  onAction,
  onOpenPanel,
  className = '',
  centerContent,
}: PanelProps) {
  // Mode state for workflow-execution-results (workflow vs results)
  const [mode, setMode] = useState<string>('results')

  // Determine if this panel supports mode switching
  const supportsMode = content.type === 'workflow-execution-results'

  // Generate actions based on content type
  const leftActions = useMemo(() => {
    const actions: TopBarAction[] = []

    // Back button always comes first
    if (onBack) {
      actions.push({
        id: TopBarActionId.PANEL_BACK,
        order: 0,
        element: (
          <TopBarButton onClick={onBack} title="Go back">
            <ArrowLeft className="h-4 w-4" />
          </TopBarButton>
        ),
      })
    }

    // Content-specific actions
    if (onAction) {
      const supportedActions = getPanelContentActions(content)

      supportedActions.forEach((action, index) => {
        let actionElement: React.ReactNode = null
        let actionId: TopBarActionId
        let title: string

        switch (action) {
          case 'download':
            actionId = TopBarActionId.PANEL_DOWNLOAD
            title = 'Download'
            actionElement = (
              <TopBarButton onClick={() => onAction('download')} title={title}>
                <Download className="h-4 w-4" />
              </TopBarButton>
            )
            break

          case 'share':
            actionId = TopBarActionId.PANEL_SHARE
            title = 'Share'
            actionElement = (
              <TopBarButton onClick={() => onAction('share')} title={title}>
                <Share className="h-4 w-4" />
              </TopBarButton>
            )
            break

          case 'export-markdown':
            actionId = TopBarActionId.PANEL_EXPORT_MARKDOWN
            title = 'Export to Markdown'
            actionElement = (
              <TopBarButton onClick={() => onAction('export-markdown')} title={title}>
                <FileText className="h-4 w-4" />
              </TopBarButton>
            )
            break

          case 'copy':
            actionId = TopBarActionId.PANEL_COPY
            title = 'Copy'
            actionElement = (
              <TopBarButton onClick={() => onAction('copy')} title={title}>
                <Copy className="h-4 w-4" />
              </TopBarButton>
            )
            break

          case 'edit':
            actionId = TopBarActionId.PANEL_EDIT
            title = 'Edit'
            actionElement = (
              <TopBarButton onClick={() => onAction('edit')} title={title}>
                <Edit className="h-4 w-4" />
              </TopBarButton>
            )
            break

          case 'fork':
            actionId = TopBarActionId.PANEL_FORK
            title = 'Fork workflow'
            actionElement = (
              <TopBarButton onClick={() => onAction('fork')} title={title}>
                <GitFork className="h-4 w-4" />
              </TopBarButton>
            )
            break

          case 'load-to-context':
            actionId = TopBarActionId.PANEL_LOAD_TO_CONTEXT
            title = 'Load to context'
            actionElement = (
              <TopBarButton onClick={() => onAction('load-to-context')} title={title}>
                <Import className="h-4 w-4" />
              </TopBarButton>
            )
            break

          default:
            return // Skip unknown actions
        }

        if (actionElement) {
          actions.push({
            id: actionId,
            order: index + 1,
            element: actionElement,
          })
        }
      })
    }

    return actions
  }, [content, onBack, onAction])

  // Right content - show toggle for workflow-execution-results, otherwise show breadcrumbs
  const rightContent = useMemo(() => {
    if (supportsMode) {
      return (
        <TopBarToggleSwitch<string>
          mode={mode}
          onModeChange={setMode}
          options={[
            { value: 'flow', label: 'Flow' },
            { value: 'results', label: 'Results' },
          ]}
        />
      )
    }
    return <PanelBreadcrumbs items={breadcrumbs} onNavigate={onNavigateTo} maxWidth="max-w-lg" />
  }, [supportsMode, mode, breadcrumbs, onNavigateTo])

  const panelContent = (
    <div className={`flex h-full w-full flex-col rounded-3xl bg-white ${className}`}>
      {/* TopBar */}
      <TopBar
        leftActions={leftActions}
        centerContent={centerContent}
        rightContent={rightContent}
        animated={true}
        animationType="stagger"
      />

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        <PanelContentRenderer content={content} className="h-full" onOpenPanel={onOpenPanel} />
      </div>
    </div>
  )

  // Wrap with mode context if needed
  if (supportsMode) {
    return (
      <PanelModeContext.Provider value={{ mode, setMode }}>
        {panelContent}
      </PanelModeContext.Provider>
    )
  }

  return panelContent
}

export type { BreadcrumbItem, PanelContent }
